import type {
    DasEditAction,
    DasOverrides,
    DasPayment,
    DasToggleOpenPayload,
    InfoMessage,
    StateTree
} from '@makemydeal/dr-dash-types';
import { paymentPayloadUtils, utils } from '@makemydeal/dr-offer-redux';
import type { GenericObject } from '@makemydeal/dr-platform-types';
import { ApplicationCodes } from '@makemydeal/dr-platform-types';
import { TRACE_ID_HEADER } from '@makemydeal/dr-shared-bff-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { RSAASuccessType } from 'redux-api-middleware';
import { RSAA } from 'redux-api-middleware';

import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { AnyAction, Dispatch } from 'redux';
import {
    ADD_DUE_AT_SIGNING_INFO_MESSAGE,
    APPLY_DUE_AT_SIGNING,
    APPLY_DUE_AT_SIGNING_FAILURE,
    APPLY_DUE_AT_SIGNING_SUCCESS,
    CLEAR_DUE_AT_SIGNING_ERRORS,
    CLEAR_DUE_AT_SIGNING_INFO_MESSAGES,
    DAS_CLEAR_PAYMENT,
    DAS_UPDATE_DOWNPAYMENT,
    DAS_UPDATE_EQUITY_ALLOCATION_METHOD,
    DAS_UPDATE_FEES_CAPITALIZED,
    DAS_UPDATE_FIRSTPAYMENT,
    DAS_UPDATE_TARGET_CASH_AMOUNT_PAID,
    DAS_UPDATE_TAXES_CAPITALIZED,
    DUE_AT_SIGNING_SLIDE_OUT_TOGGLE,
    FEES_CAPITALIZE_ALL_FEES,
    REMOVE_DUE_AT_SIGNING_INFO_MESSAGE,
    REQUEST_DUE_AT_SIGNING,
    REQUEST_DUE_AT_SIGNING_FAILURE,
    REQUEST_DUE_AT_SIGNING_SUCCESS
} from '../actionTypes/dueAtSigningActionTypes';
import { DAS_OVERRIDES_FIELDS } from '../constants';
import { getDasOverrides, getDasOverridesUpdated, getFeesCapitalizedOverride } from '../selectors/dueAtSigningSelectors';
import * as offerReduxSelectors from '../selectors/offerRedux';
import { getCurrentOfferType } from '../selectors/offerRedux';
import { getDasRequestPayload } from '../utils/dueAtSigningUtils';
import { buildPaymentPayloadHostSelectors } from '../utils/offerPayloadUtil';
import { getPaymentUrl } from '../utils/paymentServiceUtils';

type MessageActions = {
    alert: (payload: InfoMessage) => { type: string; payload: InfoMessage };
    clearMessages: () => DasEditAction;
};

export const dispatchDasOverrides = (dispatch: Dispatch<AnyAction, StateTree>, targetName: string, dasOverrides: DasOverrides) => {
    switch (targetName) {
        case DAS_OVERRIDES_FIELDS.DOWNPAYMENT: {
            dispatch(updateDasOverrides(DAS_UPDATE_DOWNPAYMENT, dasOverrides));
            break;
        }
        case DAS_OVERRIDES_FIELDS.FIRST_PAYMENT_CAPITALIZED: {
            dispatch(updateDasOverrides(DAS_UPDATE_FIRSTPAYMENT, dasOverrides));
            break;
        }
        case DAS_OVERRIDES_FIELDS.FEES_CAPITALIZED: {
            dispatch(updateDasOverrides(DAS_UPDATE_FEES_CAPITALIZED, dasOverrides));
            break;
        }
        case DAS_OVERRIDES_FIELDS.TAXES_CAPITALIZED: {
            dispatch(updateDasOverrides(DAS_UPDATE_TAXES_CAPITALIZED, dasOverrides));
            break;
        }
        case DAS_OVERRIDES_FIELDS.TARGET_CASH_AMOUNT_PAID: {
            dispatch(updateDasOverrides(DAS_UPDATE_TARGET_CASH_AMOUNT_PAID, dasOverrides));
            break;
        }
        case DAS_OVERRIDES_FIELDS.EQUITY_ALLOCATION_METHOD: {
            dispatch(updateDasOverrides(DAS_UPDATE_EQUITY_ALLOCATION_METHOD, dasOverrides));
            break;
        }
    }
};

export const updateDasChanges =
    (targetName: string) => (targetValue: any) => async (dispatch: Dispatch<AnyAction, StateTree>, getState: () => StateTree) => {
        const state = getState();

        const actions = {
            alert: addDueAtSigningInfoMessage,
            clearMessages: clearDueAtSigningInfoMessages
        };

        const dasOverrides = getDasOverridesUpdated(state, targetName, targetValue);

        dispatchMessages(state, dispatch, actions, targetName, targetValue);

        dispatchDasOverrides(dispatch, targetName, dasOverrides);
    };

export const applyDasChanges =
    (abortSignal?: AbortSignal) => async (dispatch: Dispatch<AnyAction, StateTree>, getState: () => StateTree) => {
        const state = getState();

        const dasPayload = getDasOverrides(state);
        const offerType = getCurrentOfferType(state);
        const hostSelectors = buildPaymentPayloadHostSelectors();
        const penciledInputsState = offerReduxSelectors.getPenciledInputs(state);
        const dasRequestPayload = getDasRequestPayload(dasPayload);
        const penciledInputs = utils.buildDueAtSigningPenciledInputsState(penciledInputsState, dasRequestPayload, offerType);

        const dueAtSigningState = {
            ...state,
            offer: {
                ...state.offer,
                penciledInputs
            }
        };

        const payload = paymentPayloadUtils.createPaymentPayload(dueAtSigningState, hostSelectors, {
            type: REQUEST_DUE_AT_SIGNING,
            meta: {
                sourceApplication: ApplicationCodes.MV
            }
        });

        const upqEndpoint = getPaymentUrl(state);
        const dasRequestMeta = { isDasPaymentRefreshEnabled: featureToggleSelectors.isDasPaymentRefreshEnabled(state) };

        const getDasSuccessRequest: RSAASuccessType<StateTree, DasPayment | undefined> = {
            type: REQUEST_DUE_AT_SIGNING_SUCCESS,
            meta: dasRequestMeta,
            payload: async (_action, _state, res) => {
                if (!res?.ok) {
                    return undefined;
                }

                let data: GenericObject = {};

                try {
                    data = await res.json();
                } catch (err) {
                    return undefined;
                }

                const currentOfferType = offerReduxSelectors.getCurrentOfferType(_state as StateTree);
                const payments = data[currentOfferType]?.payments || [];
                if (payments.length === 0) {
                    return undefined;
                }

                const quote = payments[0] || {};
                // Cash Down / Cash Due Today (US1833373) gated behind enableEquityAllocationOrder:
                // when enabled use the dedicated response fields (cashDueFromCustomer / netCashDown);
                // when disabled keep the legacy outOfPocket mapping.
                const isEquityAllocationOrderEnabled = featureToggleSelectors.isEquityAllocationOrderEnabled(_state as StateTree);
                const targetCashAmountPaid = isEquityAllocationOrderEnabled
                    ? quote.isTargetCashAmountPaid ?? false
                    : quote.cashOption === 'AddCashOffsetToPrice';
                const payloadValues: DasPayment = {
                    targetCashAmountPaid,
                    firstPaymentCapitalized: quote.firstPaymentCapitalized ?? false,
                    // Amount Due At Signing
                    firstPayment: quote.firstPayment ?? 0,
                    upfrontTaxTotal: quote.upfrontTaxTotal ?? 0,
                    upfrontFeesTotal: quote.upfrontFeesTotal ?? 0,
                    capCostReduction: quote.capCostReduction ?? 0,
                    upfrontAddonsTotal: quote.upfrontAddonsTotal ?? 0,
                    secDeposit: quote.securityDeposit ?? 0,
                    diaDueAtSigning: quote.diaDueAtSigning ?? 0,

                    // Amount Due at Signing Paid By
                    netTrade: quote.netTrade ?? 0,
                    totalRebates: quote.rebate ?? 0,
                    cashDueFromCustomer: isEquityAllocationOrderEnabled ? quote.cashDueFromCustomer ?? 0 : quote.outOfPocket ?? 0,
                    priorLoanBalance: quote.priorLoanBalance ?? 0,
                    totalAmountDue: quote.paidAtSigning ?? 0
                };

                return payloadValues;
            }
        };

        try {
            dispatch({
                [RSAA]: {
                    body: JSON.stringify(payload),
                    types: [
                        REQUEST_DUE_AT_SIGNING,
                        getDasSuccessRequest,
                        { type: REQUEST_DUE_AT_SIGNING_FAILURE, meta: dasRequestMeta }
                    ],
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', [TRACE_ID_HEADER]: payload.traceId ?? '' },
                    endpoint: upqEndpoint,
                    options: {
                        signal: abortSignal
                    }
                }
            });
        } catch (e) {
            const errorContent =
                e && (e as Error).message ? (e as Error).message : 'An error occurred while calculating due at signing';
            const errorMessage = {
                heading: 'Due at Signing',
                content: errorContent
            };

            dispatch(addDueAtSigningInfoMessage(errorMessage));
            dispatch({
                type: REQUEST_DUE_AT_SIGNING_FAILURE,
                payload: [errorMessage]
            });
        }
    };

export const clearDasErrors = () => {
    return {
        type: CLEAR_DUE_AT_SIGNING_ERRORS
    };
};

export const updateDasOverrides = (type: string, payload: DasOverrides) => {
    return {
        type,
        payload
    };
};

export const addDueAtSigningInfoMessage = (message: { heading: string; content: string }) => {
    return {
        type: ADD_DUE_AT_SIGNING_INFO_MESSAGE,
        payload: message
    };
};

export const removeDueAtSigningInfoMessage = (index: number) => {
    return {
        type: REMOVE_DUE_AT_SIGNING_INFO_MESSAGE,
        payload: index
    };
};

export const clearDueAtSigningInfoMessages = () => {
    return {
        type: CLEAR_DUE_AT_SIGNING_INFO_MESSAGES
    };
};

export const showDueAtSigningSuccessMessage = () => {
    return {
        type: APPLY_DUE_AT_SIGNING_SUCCESS
    };
};

export const getDueAtSigningApply = (abortSignal?: AbortSignal) => (dispatch: any, getState: any) => {
    const state = getState();

    const dasOverrides = getDasOverrides(state);
    const dasPayload = getDasRequestPayload(dasOverrides);

    dispatch({
        type: APPLY_DUE_AT_SIGNING,
        payload: dasPayload,
        meta: {
            middleware: {
                payment: true
            },
            byPassAbortError: true,
            abortSignal
        }
    });
};

export const applyDueAtSigningSuccess =
    () =>
    async (dispatch: Dispatch<AnyAction, StateTree>): Promise<void> => {
        dispatch(showDueAtSigningSuccessMessage());
    };

export const applyDueAtSigningError =
    (_action: AnyFSA, messages?: GenericObject[]) =>
    async (dispatch: Dispatch<AnyAction, StateTree>): Promise<void> => {
        dispatch({
            type: APPLY_DUE_AT_SIGNING_FAILURE,
            payload: messages
        });
    };

export const dispatchMessages = (
    state: StateTree,
    dispatch: Dispatch<AnyAction, StateTree>,
    actions: MessageActions,
    targetName: string,
    targetValue: boolean | number
) => {
    const feesConvertedToUpfront = 'All Fees have been converted to upfront';
    const taxesOrFeesDisplayCategory = 'Taxes or Fees';
    const unselectCapitalizeTaxOrFees = `Deselecting "Capitalize" for ${taxesOrFeesDisplayCategory} will restore the configuration currently set in the deal`;

    dispatch(actions.clearMessages());

    const isUncheckingFees = targetName === DAS_OVERRIDES_FIELDS.FEES_CAPITALIZED && !targetValue;
    const isUncheckingTaxes = targetName === DAS_OVERRIDES_FIELDS.TAXES_CAPITALIZED && !targetValue;

    if (isUncheckingFees && getFeesCapitalizedOverride(state)) {
        dispatch(
            actions.alert({
                heading: taxesOrFeesDisplayCategory,
                content: feesConvertedToUpfront
            })
        );
    } else if (isUncheckingFees || isUncheckingTaxes) {
        dispatch(
            actions.alert({
                heading: taxesOrFeesDisplayCategory,
                content: unselectCapitalizeTaxOrFees
            })
        );
    }
};

export const toggleDasSlideOut = (payload?: DasToggleOpenPayload) => {
    return {
        type: DUE_AT_SIGNING_SLIDE_OUT_TOGGLE,
        payload
    };
};

export const clearDasPayment = () => ({
    type: DAS_CLEAR_PAYMENT
});

export const capitalizeAllFees = (isCapitalized: boolean) => ({
    type: FEES_CAPITALIZE_ALL_FEES,
    payload: { isCapitalized }
});
